import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, Eye, Send, FileSignature, Save } from "lucide-react";
import { MilestoneBillingGrid } from "@/components/billing/MilestoneBillingGrid";
import { MilestoneOverrideDialog } from "@/components/billing/MilestoneOverrideDialog";
import { OwnerReviewDialog } from "@/components/billing/OwnerReviewDialog";
import { INVOICE_STATUS } from "@/pages/ContractsPage";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [overridesOpen, setOverridesOpen] = useState(false);

  const load = useCallback(() => axios.get(`${API}/invoices/${invoiceId}`).then(r => setInv(r.data)).catch(() => toast.error("Invoice not found")), [invoiceId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!inv || user?.role !== "owner") return;
    const reviewId = new URLSearchParams(window.location.search).get("review");
    const milestone = inv.payment_schedule?.find(item => item.milestone_id === reviewId && item.workflow_status === "awaiting_review");
    if (milestone) setReview(milestone);
  }, [inv, user?.role]);

  const togglePaid = async (m) => {
    let body = {};
    if (m.status !== "paid") {
      const method = window.prompt("How was this paid? (a receipt will be emailed to the customer)", "e-transfer");
      if (method === null) return;
      body = { method, send_receipt: !!inv.customer_email };
    }
    try { const r = await axios.post(`${API}/invoices/${invoiceId}/milestones/${m.milestone_id}/pay`, body); setInv(r.data); toast.success(m.status === "paid" ? "Payment reverted" : `Marked paid: ${money(m.amount)}${body.send_receipt ? " · receipt emailed" : ""}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const setDue = async (m, due_date) => {
    try { const r = await axios.put(`${API}/invoices/${invoiceId}/milestones/${m.milestone_id}/due-date`, { due_date }); setInv(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to set due date"); }
  };
  const save = async () => {
    setBusy(true);
    try { const r = await axios.put(`${API}/invoices/${invoiceId}`, { notes: inv.notes }); setInv({ ...inv, ...r.data }); toast.success("Invoice notes saved"); }
    catch (e) { if (e.response?.status === 202) toast.info("Change sent to owner for approval"); else toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const sendInvoice = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/invoices/${invoiceId}/send`); toast.success(`Invoice emailed to ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const createContract = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/contracts/from-invoice/${invoiceId}`); navigate(`/contracts/${r.data.contract_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to create contract"); }
    finally { setBusy(false); }
  };

  if (!inv) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="invoice-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-testid="invoice-title">Invoice {inv.invoice_number} <Badge className={INVOICE_STATUS[inv.status]}>{inv.status.replace("_", " ")}</Badge></h1>
            <p className="text-xs text-slate-500">{inv.customer_name} · from {inv.estimate_number}{inv.lead_id && <> · <button className="underline" onClick={() => navigate(`/customers/${inv.lead_id}`)}>customer profile</button></>}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" asChild><a href={`${API}/invoices/${invoiceId}/pdf`} target="_blank" rel="noreferrer" data-testid="invoice-pdf-btn"><Eye className="w-4 h-4 mr-1" /> PDF</a></Button>
          <Button variant="outline" size="sm" onClick={sendInvoice} disabled={busy || !inv.customer_email || inv.review_state === "awaiting_review"} data-testid="invoice-send-btn"><Send className="w-4 h-4 mr-1" /> Email invoice</Button>
          {(user?.role === "owner" || !user?.role) && <Button variant="outline" size="sm" onClick={() => setOverridesOpen(true)} data-testid="milestone-override-button">Override schedule</Button>}
          {inv.contract ? (
            <Button size="sm" variant="outline" onClick={() => navigate(`/contracts/${inv.contract.contract_id}`)} data-testid="invoice-open-contract"><FileSignature className="w-4 h-4 mr-1" /> Contract {inv.contract.contract_number} ({inv.contract.status})</Button>
          ) : (
            <Button size="sm" onClick={createContract} disabled={busy} data-testid="invoice-create-contract"><FileSignature className="w-4 h-4 mr-1" /> Create Contract</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Total" value={money(inv.total)} />
        <Stat label="Paid" value={money(inv.amount_paid)} className="text-green-600" testId="invoice-paid" />
        <Stat label="Balance due" value={money(inv.balance_due)} className={inv.balance_due > 0 ? "text-amber-600" : "text-green-600"} testId="invoice-balance" />
      </div>

      <Card><CardHeader><CardTitle className="text-base">Payment schedule</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <MilestoneBillingGrid milestones={inv.payment_schedule || []} isOwner={user?.role === "owner" || !user?.role} onReview={setReview} onOverride={() => setOverridesOpen(true)} onTogglePaid={togglePaid} onDueDate={setDue} />
          <p className="text-xs text-slate-400">Set a due date on a milestone to enable automatic payment reminders (before and on the due date).</p>
          <div className="flex justify-between items-end gap-3">
            <div className="flex-1"><Label className="text-xs">Notes (printed on invoice)</Label><Textarea rows={2} value={inv.notes || ""} onChange={e => setInv({ ...inv, notes: e.target.value })} data-testid="invoice-notes" /></div>
            <Button size="sm" onClick={save} disabled={busy} data-testid="invoice-save-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        </CardContent></Card>

      <Card><CardHeader><CardTitle className="text-base">Line items</CardTitle></CardHeader>
        <CardContent className="divide-y text-sm">
          {(inv.line_items || []).map((li, i) => (
            <div key={i} className="flex justify-between py-2 gap-3"><span className="text-slate-700">{li.description}{li.quantity > 1 && <span className="text-slate-400"> × {li.quantity}</span>}</span><span className="font-medium shrink-0">{money(li.total ?? li.quantity * li.unit_price)}</span></div>
          ))}
          <div className="pt-3 space-y-1 text-right">
            <p className="text-slate-500">Subtotal {money(inv.subtotal)}</p>
            {inv.discount > 0 && <p className="text-slate-500">Discount −{money(inv.discount)}</p>}
            <p className="text-slate-500">Tax ({inv.tax_rate}%) {money(inv.tax_amount)}</p>
            <p className="font-bold text-slate-900">Total {money(inv.total)}</p>
          </div>
        </CardContent></Card>
      <MilestoneOverrideDialog open={overridesOpen} onOpenChange={setOverridesOpen} invoice={inv} onSaved={updated => setInv(updated)} />
      <OwnerReviewDialog open={!!review} onOpenChange={open => { if (!open) setReview(null); }} invoice={inv} milestone={review} onSent={updated => setInv(updated)} />
    </div>
  );
}

const Stat = ({ label, value, className = "", testId }) => (
  <Card data-testid={testId}><CardContent className="pt-5"><p className={`text-2xl font-bold ${className || "text-slate-900"}`}>{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>
);
